import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  CardItem,
  DataGrid,
  DebouncedSearchInput,
  ListHeader,
  ModalOpenButton,
} from "@msys/ui";
import { Stack, TablePagination, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { omitBy } from "lodash-es";
import { useNavigate } from "react-router-dom";
import { PimSearchSuppliersSorting } from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { Avatar } from "../../commons/Avatar.js";
import { ButtonCreate } from "../../commons/button/Button.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../constants.js";
import { SupplierCreateModal } from "../../features/suppliers/SupplierCreateModal.js";
import { SupplierListItem } from "../../features/suppliers/SupplierListItem.js";
import {
  SuppliersQueryVariables,
  useSuppliersQuery,
} from "./Suppliers.generated.js";

const ALLOWED_VIEWS: CollectionView[] = ["table", "list"];
type Filters = Omit<SuppliersQueryVariables, "limit" | "offset" | "sorting">;
const DEFAULT_SORTING: PimSearchSuppliersSorting[] = [
  {
    column: "name",
    direction: "asc",
  },
];

interface Props {
  submenuItems: PageTopbarItem[];
}

export const Suppliers = ({ submenuItems }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslate(["Global", "CrmOrganisations", "SupplierNew"]);

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>("suppliers", "table", "list");

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
  } = useFiltersAndPagination<PimSearchSuppliersSorting, Filters>(
    {},
    DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE
  );

  const QUERY_BASE_VARIABLES = {
    offset,
    limit,
    sorting: DEFAULT_SORTING,
  };

  const client = useApolloClient();
  const query = useSuppliersQuery({
    client,
    variables: {
      ...QUERY_BASE_VARIABLES,
      ...omitBy(filters, filter => (filter ? filter.length === 0 : true)),
      sorting,
    },
  });

  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.suppliers)?.totalCount ??
    0;
  const suppliers =
    getDataOrNull((query.data ?? query.previousData)?.suppliers)?.suppliers ??
    [];

  // Show the onboarding empty-state only when the organisation genuinely has no
  // subscribed suppliers — not when a search simply returned no matches.
  const isEmpty =
    !query.loading && totalCount === 0 && !filters.filterSearchTerm;

  // On save, stay on the suppliers list and refetch it once after the batch,
  // rather than force-navigating into a single supplier's profile.
  const createSupplierModalProps = {
    handleComplete: async (handleClose: () => void) => {
      await query.refetch();
      handleClose();
    },
  };

  return (
    <Page submenuItems={submenuItems}>
      <PageContainer>
        <ListHeader
          sx={{ marginBottom: 2 }}
          SwitchViewButton={
            <SwitchCollectionViewButton
              allowedViews={ALLOWED_VIEWS}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          }
          CreateButton={
            <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
              <ModalOpenButton
                Modal={SupplierCreateModal}
                modalProps={createSupplierModalProps}
              >
                <ButtonCreate
                  variant="contained"
                  color="primary"
                  title={t("New supplier", {
                    ns: "CrmOrganisations",
                  })}
                />
              </ModalOpenButton>
            </RestrictedByOrganisationPermissionWithDebug>
          }
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", {
                ns: "Global",
              })}
              defaultValue={filters.filterSearchTerm}
              onChangeSearchTerm={newValue =>
                setFilters(filters => ({
                  ...filters,
                  filterSearchTerm: newValue,
                }))
              }
            />
          }
        />
        {isEmpty ? (
          <Stack spacing={2} sx={{ alignItems: "center", paddingY: 8 }}>
            <Typography variant="body1" color="text.secondary">
              {t("Add suppliers to access their catalogues.", {
                ns: "SupplierNew",
              })}
            </Typography>
            <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
              <ModalOpenButton
                Modal={SupplierCreateModal}
                modalProps={createSupplierModalProps}
              >
                <ButtonCreate
                  variant="contained"
                  color="primary"
                  title={t("New supplier", {
                    ns: "CrmOrganisations",
                  })}
                />
              </ModalOpenButton>
            </RestrictedByOrganisationPermissionWithDebug>
          </Stack>
        ) : activeView === "table" ? (
          <DataGrid
            rows={suppliers}
            columns={[
              {
                field: "avatar",
                headerName: "",
                flex: 0,
                width: 50,
                renderCell: ({ row: supplier }) => (
                  <Avatar
                    url={supplier.logo?.url}
                    initials={supplier.title.substring(0, 2)}
                    size="s"
                    type="square"
                  />
                ),
              },
              {
                field: "title",
                headerName: t("Name", {
                  ns: "CrmOrganisations",
                }),
                flex: 1,
              },
            ]}
            onRowClick={({ row: supplier }) => {
              navigate(`/products/suppliers/${supplier.id}`);
            }}
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
          />
        ) : (
          <Stack direction="column" spacing={1}>
            {suppliers.map(supplier => (
              <CardItem
                key={supplier.id}
                onClick={() => {
                  navigate(`/products/suppliers/${supplier.id}`);
                }}
              >
                <SupplierListItem supplier={supplier} />
              </CardItem>
            ))}
            <TablePagination
              component="div"
              count={totalCount}
              page={paginationModel.page}
              onPageChange={(event, newPage) => {
                setPaginationModel({
                  pageSize: paginationModel.pageSize,
                  page: newPage,
                });
              }}
              rowsPerPage={paginationModel.pageSize}
              onRowsPerPageChange={event => {
                setPaginationModel({
                  pageSize: parseInt(event.target.value, 10),
                  page: 0,
                });
              }}
              labelRowsPerPage={t("Per page:", { ns: "Global" })}
            />
          </Stack>
        )}
      </PageContainer>
    </Page>
  );
};
